import { useEffect, useState } from 'react';
import Card from '../common/Card';
import Badge from '../common/Badge';

/**
 * PortalPreview - Live WYSIWYG preview of the portal page
 * Shows real-time updates as sections are configured
 * Supports multi-column layout
 */
export default function PortalPreview({ sections, portalData, teamSlug }) {
  const [filteredSections, setFilteredSections] = useState([]);

  useEffect(() => {
    // Filter to only visible sections and sort by sort_order
    const visible = sections
      .filter(s => s.visible)
      .sort((a, b) => a.sort_order - b.sort_order);
    setFilteredSections(visible);
  }, [sections]);

  if (!portalData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Loading portal data...</p>
      </div>
    );
  }

  const renderSection = (section) => {
    switch (section.section_type) {
      case 'dispatches':
        return renderDispatches(section);
      case 'wiki':
        return renderWiki(section);
      case 'calendar':
        return renderCalendar(section);
      case 'roster':
        return renderRoster(section);
      case 'files':
        return renderFiles(section);
      case 'snacks':
        return renderSnacks(section);
      case 'custom':
        return renderCustom(section);
      case 'links':
        return renderLinks(section);
      case 'gallery':
        return renderGallery(section);
      case 'videos':
        return renderVideos(section);
      case 'attachments':
        return renderAttachments(section);
      case 'contact_form':
        return renderContactForm(section);
      case 'event_calendar':
        return renderEventCalendar(section);
      case 'social_feed':
        return renderSocialFeed(section);
      default:
        return null;
    }
  };

  const renderDispatches = (section) => {
    const limit = section.section_config?.limit || 10;
    const dispatches = portalData.dispatches?.slice(0, limit) || [];

    if (dispatches.length === 0) {
      return (
        <Card>
          <p className="text-center text-gray-500 py-4 text-sm">
            No dispatches yet.
          </p>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {dispatches.map((dispatch) => (
          <Card key={dispatch.id}>
            {dispatch.pinned && (
              <div className="mb-2">
                <Badge variant="warning">Pinned</Badge>
              </div>
            )}
            <h3 className="font-semibold text-lg mb-2">{dispatch.title}</h3>
            <p className="text-sm text-gray-600 mb-2">
              {new Date(dispatch.sent_at).toLocaleDateString()}
            </p>
            {dispatch.excerpt && (
              <p className="text-sm text-gray-700">{dispatch.excerpt}</p>
            )}
          </Card>
        ))}
      </div>
    );
  };

  const renderWiki = (section) => {
    const wikiItems = portalData.wiki_items || [];

    if (wikiItems.length === 0) {
      return (
        <Card>
          <p className="text-center text-gray-500 py-4 text-sm">
            No quick info yet.
          </p>
        </Card>
      );
    }

    // Group by category
    const grouped = wikiItems.reduce((acc, item) => {
      const category = item.category || 'Quick Notes';
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {});

    return (
      <div className="space-y-4">
        {Object.entries(grouped).map(([category, items]) => (
          <div key={category}>
            {Object.keys(grouped).length > 1 && (
              <h3 className="font-medium text-sm text-gray-600 mb-2">
                {category}
              </h3>
            )}
            <div className="space-y-2">
              {items.map((item) => (
                <Card key={item.id} className={item.is_pinned ? 'border-2 border-amber-300' : ''}>
                  {item.is_pinned && (
                    <div className="mb-2">
                      <Badge variant="warning">Pinned</Badge>
                    </div>
                  )}
                  <h4 className="font-medium text-gray-900 mb-1">{item.title}</h4>
                  <div 
                    className="text-sm text-gray-700 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  />
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCalendar = (section) => {
    const limit = section.section_config?.limit || 10;
    const events = portalData.calendar_events?.slice(0, limit) || [];

    if (events.length === 0) {
      return (
        <Card>
          <p className="text-center text-gray-500 py-4 text-sm">
            No upcoming events.
          </p>
        </Card>
      );
    }

    return (
      <div className="space-y-3">
        {events.map((event) => (
          <Card key={event.id}>
            {event.pinned && (
              <div className="mb-2">
                <Badge variant="warning">Pinned</Badge>
              </div>
            )}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {new Date(event.start_time).getDate()}
                </div>
                <div className="text-xs text-gray-600 uppercase">
                  {new Date(event.start_time).toLocaleDateString('en-US', { month: 'short' })}
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">{event.title}</h4>
                <p className="text-sm text-gray-600">
                  {new Date(event.start_time).toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit' 
                  })}
                </p>
                {event.location && (
                  <p className="text-sm text-gray-600 mt-1">📍 {event.location}</p>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  const renderRoster = (section) => {
    const roster = portalData.roster || [];
    if (roster.length === 0) {
      return <Card><p className="text-center text-gray-500 py-4 text-sm">No roster yet.</p></Card>;
    }

    return (
      <Card>
        <div className="space-y-2">
          {roster.map((player) => (
            <div key={player.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
              <span className="font-medium">{player.player_name}</span>
              <div className="flex gap-3 text-sm text-gray-600">
                {player.jersey_number && <span>#{player.jersey_number}</span>}
                {player.position && <span>{player.position}</span>}
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  const renderFiles = (section) => {
    const files = portalData.files || [];
    if (files.length === 0) {
      return <Card><p className="text-center text-gray-500 py-4 text-sm">No files yet.</p></Card>;
    }

    return (
      <Card>
        <div className="space-y-3">
          {files.map((file) => (
            <div key={file.id} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <span className="text-2xl">📄</span>
                <div>
                  <p className="font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-600">
                    {(file.file_size / 1024).toFixed(1)} KB • {new Date(file.uploaded_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  const renderSnacks = (section) => {
    return (
      <Card>
        <p className="text-center text-gray-500 py-4 text-sm">
          Snack schedule display
        </p>
      </Card>
    );
  };

  const renderCustom = (section) => {
    const html = section.section_config?.custom_html || '';
    if (!html) {
      return (
        <Card>
          <p className="text-center text-gray-500 py-4 text-sm">
            Add custom content in the editor
          </p>
        </Card>
      );
    }

    return (
      <Card>
        <div 
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </Card>
    );
  };

  const renderLinks = (section) => {
    const links = (portalData.portal_links || [])
      .filter((link) => link.section_id === section.id && link.visible !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    
    if (links.length === 0) {
      return (
        <Card>
          <p className="text-center text-gray-500 py-4 text-sm">
            No links added yet.
          </p>
        </Card>
      );
    }

    return (
      <Card>
        <div className="space-y-4">
          {links.map((link) => (
            <div key={link.id} className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-medium text-base hover:underline inline-flex items-center gap-1"
              >
                {link.title}
                <span className="text-sm">↗</span>
              </a>
              {link.description && (
                <p className="text-sm text-gray-600 mt-1">{link.description}</p>
              )}
            </div>
          ))}
        </div>
      </Card>
    );
  };

  const renderGallery = (section) => {
    const items = (portalData.portal_gallery_items || [])
      .filter((item) => item.section_id === section.id && item.visible !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    if (items.length === 0) {
      return (
        <Card>
          <p className="text-center text-gray-500 py-4 text-sm">No images yet.</p>
        </Card>
      );
    }

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {items.map((item) => (
          <div key={item.id} className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <img src={item.image_url} alt={item.title || 'Gallery'} className="w-full h-40 object-cover" />
            {(item.title || item.caption) && (
              <div className="p-2">
                {item.title && <p className="text-sm font-medium text-gray-900">{item.title}</p>}
                {item.caption && <p className="text-xs text-gray-600 mt-1">{item.caption}</p>}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderVideos = (section) => {
    const videos = (portalData.portal_video_items || [])
      .filter((item) => item.section_id === section.id && item.visible !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    if (videos.length === 0) {
      return (
        <Card>
          <p className="text-center text-gray-500 py-4 text-sm">No videos added yet.</p>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {videos.map((video) => (
          <Card key={video.id} className="overflow-hidden">
            <div className="aspect-video bg-black">
              {video.embed_url ? (
                <iframe
                  src={video.embed_url}
                  title={video.title || 'Video'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-white text-sm">
                  Unable to preview video
                </div>
              )}
            </div>
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 text-lg">{video.title || 'Untitled video'}</h3>
              {video.description && <p className="text-sm text-gray-600 mt-2">{video.description}</p>}
            </div>
          </Card>
        ))}
      </div>
    );
  };

  const renderAttachments = (section) => {
    const attachments = (portalData.portal_attachment_items || [])
      .filter((item) => item.section_id === section.id && item.visible !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    if (attachments.length === 0) {
      return (
        <Card>
          <p className="text-center text-gray-500 py-4 text-sm">No files uploaded yet.</p>
        </Card>
      );
    }

    return (
      <Card>
        <ul className="divide-y divide-gray-200">
          {attachments.map((file) => (
            <li key={file.id} className="py-3 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 text-sm">{file.file_name}</p>
                <p className="text-xs text-gray-500">{formatFileSize(file.file_size)}</p>
              </div>
              <a
                href={file.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Download
              </a>
            </li>
          ))}
        </ul>
      </Card>
    );
  };

  const renderContactForm = (section) => {
    const config = section.section_config?.contactForm || {
      collectPhone: true,
      successMessage: 'Thanks for reaching out!'
    };

    return (
      <Card>
        <form className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Your Name</label>
            <input
              type="text"
              placeholder="Jane Parent"
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded"
              disabled
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              placeholder="you@example.com"
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded"
              disabled
            />
          </div>
          {config.collectPhone && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone (optional)</label>
              <input
                type="tel"
                placeholder="555-123-4567"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded"
                disabled
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700">Message</label>
            <textarea
              rows={4}
              placeholder="Let us know how we can help..."
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded"
              disabled
            />
          </div>
          <button
            type="button"
            className="w-full px-4 py-2 bg-blue-600 text-white rounded cursor-not-allowed"
            disabled
          >
            Submit
          </button>
          <p className="text-xs text-gray-500 text-center">{config.successMessage}</p>
        </form>
      </Card>
    );
  };

  const renderEventCalendar = (section) => {
    const config = section.section_config?.eventCalendar || {
      weeksAhead: 4,
      includePastWeeks: 0,
      groupBy: 'month',
      showLocations: true
    };

    const events = (portalData.calendar_events || []).map((event) => ({ ...event, date: new Date(event.start_time) }));
    const startWindow = new Date();
    startWindow.setDate(startWindow.getDate() - (config.includePastWeeks || 0) * 7);
    const endWindow = new Date();
    endWindow.setDate(endWindow.getDate() + (config.weeksAhead || 4) * 7);

    const filtered = events
      .filter((event) => event.date >= startWindow && event.date <= endWindow)
      .sort((a, b) => a.date - b.date);

    if (filtered.length === 0) {
      return (
        <Card>
          <p className="text-center text-gray-500 py-4 text-sm">No events scheduled in this range.</p>
        </Card>
      );
    }

    const grouped = groupEvents(filtered, config.groupBy);

    return (
      <div className="space-y-4">
        {Object.entries(grouped).map(([groupLabel, groupEvents]) => (
          <Card key={groupLabel}>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">{groupLabel}</h3>
            <ul className="space-y-2">
              {groupEvents.map((event) => (
                <li key={event.id} className="border border-gray-200 rounded-md p-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{event.title}</p>
                      <p className="text-xs text-gray-500">
                        {formatEventDate(event.date)} • {event.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </p>
                      {config.showLocations && event.location && (
                        <p className="text-xs text-gray-500 mt-1">📍 {event.location}</p>
                      )}
                    </div>
                    {event.description && (
                      <p className="text-xs text-gray-600 sm:w-1/2">{event.description}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
    );
  };

  const renderSocialFeed = (section) => {
    const items = (portalData.portal_social_links || [])
      .filter((item) => item.section_id === section.id && item.visible !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    if (items.length === 0) {
      return (
        <Card>
          <p className="text-center text-gray-500 py-4 text-sm">No social items configured.</p>
        </Card>
      );
    }

    return (
      <div className="space-y-3">
        {items.map((item) => (
          <Card key={item.id} className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-0.5 text-[11px] rounded bg-purple-100 text-purple-700">
                {item.platform || 'Social'}
              </span>
              {item.display_name && <p className="text-sm font-medium text-gray-900">{item.display_name}</p>}
            </div>
            {item.profile_url && (
              <a
                href={item.profile_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline break-all"
              >
                {item.profile_url}
              </a>
            )}
            {item.embed_html && (
              <div
                className="mt-2 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: item.embed_html }}
              />
            )}
          </Card>
        ))}
      </div>
    );
  };

  const groupEvents = (events, mode) => {
    const groups = {};
    events.forEach((event) => {
      let key = 'Events';
      if (mode === 'week') {
        const startOfWeek = new Date(event.date);
        const day = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
        startOfWeek.setDate(diff);
        key = `Week of ${startOfWeek.toLocaleDateString()}`;
      } else if (mode === 'month') {
        key = event.date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      } else if (mode === 'day') {
        key = event.date.toLocaleDateString();
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(event);
    });
    return groups;
  };

  const formatFileSize = (bytes = 0) => {
    if (!bytes) return '0 KB';
    const units = ['B', 'KB', 'MB', 'GB'];
    const idx = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, idx);
    return `${value.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
  };

  const formatEventDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Group sections by layout type
  const mainSections = filteredSections.filter(s => s.section_config?.layout !== 'sidebar');
  const sidebarSections = filteredSections.filter(s => s.section_config?.layout === 'sidebar');

  return (
    <div className="relative min-h-full bg-white">
      {/* Portal Layout */}
      <div className="max-w-6xl mx-auto p-6 min-h-full">
        {/* Team Name */}
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {portalData.team?.name || 'Team Name'}
          </h1>
          <p className="text-gray-600">Team Portal</p>
        </div>

        {filteredSections.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No visible sections configured</p>
            <p className="text-sm text-gray-400 mt-2">Add sections to see them here</p>
          </div>
        ) : sidebarSections.length > 0 ? (
          /* Two-column layout when sidebar sections exist */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content - 2/3 width */}
            <div className="lg:col-span-2 space-y-6">
              {mainSections.map(section => (
                <section key={section.id} className="border-b border-gray-200 last:border-0 pb-6 last:pb-0">
                  <h2 className="text-2xl font-bold mb-4">{section.title}</h2>
                  {renderSection(section)}
                </section>
              ))}
            </div>
            
            {/* Sidebar - 1/3 width */}
            <div className="space-y-6">
              {sidebarSections.map(section => (
                <section key={section.id} className="border-b border-gray-200 last:border-0 pb-6 last:pb-0">
                  <h2 className="text-xl font-bold mb-4">{section.title}</h2>
                  {renderSection(section)}
                </section>
              ))}
            </div>
          </div>
        ) : (
          /* Single column layout when no sidebar sections */
          <div className="space-y-6">
            {mainSections.map(section => (
              <section key={section.id} className="border-b border-gray-200 last:border-0 pb-6 last:pb-0">
                <h2 className="text-2xl font-bold mb-4">{section.title}</h2>
                {renderSection(section)}
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

